import { useState, useEffect, useCallback, useRef } from 'react'

export interface Notification {
  id: string
  type: string
  subject: string
  body: string
  readAt: string | null
  createdAt: string
  refId?: string
  refType?: string
}

interface NotificationResponse {
  notifications: Notification[]
  unreadCount: number
}

export interface UseNotificationsOptions {
  apiBase: string // e.g. 'https://api-staging.swimflows.com'
  getAuthHeader: () => string | null // returns 'Bearer token' or null if cookie-handled
  enabled?: boolean // default true
  sseEnabled?: boolean // default false — set true only when SSE feature is built and deployed
  workspaceId?: string // optional workspace filter
  onDisputeEvent?: (eventType: 'opened' | 'message.received' | 'escalated' | 'resolved') => void
}

export interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  refetch: () => void
}

export function useNotifications(options: UseNotificationsOptions): UseNotificationsReturn {
  const { apiBase, getAuthHeader, enabled = true, sseEnabled = false, workspaceId, onDisputeEvent } = options
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const esRef = useRef<EventSource | null>(null)
  const activeRef = useRef(true)

  // Refs for unstable props — lets the effect run once without re-triggering on every render
  const getAuthHeaderRef = useRef(getAuthHeader)
  const onDisputeEventRef = useRef(onDisputeEvent)
  const workspaceIdRef = useRef(workspaceId)
  useEffect(() => { getAuthHeaderRef.current = getAuthHeader }, [getAuthHeader])
  useEffect(() => { onDisputeEventRef.current = onDisputeEvent }, [onDisputeEvent])
  useEffect(() => { workspaceIdRef.current = workspaceId }, [workspaceId])

  const buildHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const auth = getAuthHeaderRef.current()
    if (auth) headers['Authorization'] = auth
    return headers
  }, [])

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '20' })
      if (workspaceIdRef.current) params.append('workspaceId', workspaceIdRef.current)
      const res = await fetch(`${apiBase}/api/notifications?${params.toString()}`, {
        credentials: 'include',
        headers: buildHeaders(),
      })
      if (!res.ok) return
      const data = (await res.json()) as NotificationResponse
      setNotifications(data.notifications)
      setUnreadCount(data.unreadCount ?? data.notifications.filter(n => !n.readAt).length)
    } finally {
      setLoading(false)
    }
  }, [apiBase, buildHeaders])

  // SSE subscription — effect only re-runs when apiBase or enabled changes
  useEffect(() => {
    if (!enabled) return

    activeRef.current = true
    let retryDelay = 5000

    async function connectSse() {
      if (!activeRef.current) return

      try {
        // Get a stream token first (SSE uses GET, can't send auth headers directly)
        const tokenRes = await fetch(`${apiBase}/api/notifications/stream-token`, {
          method: 'POST',
          credentials: 'include',
          headers: buildHeaders(),
        })

        // 401 = not authenticated, 503 = feature disabled — stop retrying permanently
        if (tokenRes.status === 401 || tokenRes.status === 503 || !activeRef.current) return

        if (!tokenRes.ok) {
          // Transient error — retry with backoff
          if (activeRef.current) {
            retryDelay = Math.min(retryDelay * 2, 60000)
            setTimeout(() => void connectSse(), retryDelay)
          }
          return
        }

        retryDelay = 5000 // reset on success
        const { token } = (await tokenRes.json()) as { token: string }

        const es = new EventSource(`${apiBase}/api/notifications/stream?token=${token}`)
        esRef.current = es

        const refetch = () => {
          if (activeRef.current) void fetchNotifications()
        }

        es.addEventListener('notification', refetch)
        es.addEventListener('dispute.opened', () => {
          refetch()
          onDisputeEventRef.current?.('opened')
        })
        es.addEventListener('dispute.message.received', () => {
          refetch()
          onDisputeEventRef.current?.('message.received')
        })
        es.addEventListener('dispute.escalated', () => {
          refetch()
          onDisputeEventRef.current?.('escalated')
        })
        es.addEventListener('dispute.resolved', () => {
          refetch()
          onDisputeEventRef.current?.('resolved')
        })

        es.onerror = () => {
          es.close()
          esRef.current = null
          if (activeRef.current) {
            retryDelay = Math.min(retryDelay * 2, 60000)
            setTimeout(() => void connectSse(), retryDelay)
          }
        }
      } catch {
        // Network failure — retry with backoff
        if (activeRef.current) {
          retryDelay = Math.min(retryDelay * 2, 60000)
          setTimeout(() => void connectSse(), retryDelay)
        }
      }
    }

    void fetchNotifications()
    if (sseEnabled) void connectSse()

    return () => {
      activeRef.current = false
      esRef.current?.close()
      esRef.current = null
    }
  }, [enabled, apiBase, fetchNotifications, buildHeaders])

  const markRead = useCallback(
    async (id: string) => {
      await fetch(`${apiBase}/api/notifications/${id}/read`, {
        method: 'PATCH',
        credentials: 'include',
        headers: buildHeaders(),
      })
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    },
    [apiBase, buildHeaders]
  )

  const markAllRead = useCallback(async () => {
    await fetch(`${apiBase}/api/notifications/read-all`, {
      method: 'POST',
      credentials: 'include',
      headers: buildHeaders(),
    })
    setNotifications(prev =>
      prev.map(n => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
    )
    setUnreadCount(0)
  }, [apiBase, buildHeaders])

  return { notifications, unreadCount, loading, markRead, markAllRead, refetch: fetchNotifications }
}
